import React, { useState, useMemo } from 'react';
import CalcShell, { StepsPanel } from '../CalcShell';
import { fmt, PieChart, BalanceGraph, AmortTable, buildAmortization } from './_finHelpers';

// HELOC Calculator - Draw period + Repayment period
export default function HELOCCalc() {
  const [amount, setAmount] = useState(50000);
  const [rate, setRate] = useState(8);
  const [draw, setDraw] = useState(5);
  const [repay, setRepay] = useState(15);
  const [includeCosts, setIncludeCosts] = useState(false);
  const [costs, setCosts] = useState(1500);
  const [showMore, setShowMore] = useState(false);
  const [propTax, setPropTax] = useState(0);
  const [homeIns, setHomeIns] = useState(0);
  const [biweekly, setBiweekly] = useState(false);
  const c = useMemo(() => {
    const principal = amount + (includeCosts ? costs : 0);
    const r = rate/100/12;
    const drawMonthly = principal * r; // interest-only during draw
    const nR = repay * 12;
    const repayMonthly = r === 0 ? principal/nR : (principal*r*Math.pow(1+r,nR))/(Math.pow(1+r,nR)-1);
    const drawTotal = drawMonthly * draw * 12;
    const amort = buildAmortization(principal, rate, nR);
    const totalPmts = draw*12 + nR;
    return { drawMonthly, repayMonthly, drawTotal, amort, totalPmts, totalPaid: drawTotal + amort.totalPaid, totalInt: drawTotal + amort.totalInterest };
  }, [amount, rate, draw, repay, includeCosts, costs]);
  return (
    <>
      <CalcShell look="cnet" title="HELOC Calculator" subtitle="Estimate interest-only draw period payments and amortized repayment period payments for a Home Equity Line of Credit."
        about={<>
          <p>A <b>Home Equity Line of Credit (HELOC)</b> is a revolving credit line secured by your home. During the <b>draw period</b> (often 5–10 years), you can borrow and typically pay interest only. During the <b>repayment period</b>, you repay principal and interest on a fixed schedule.</p>
        </>}
      >
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div>
            <div className="field"><label>Loan amount</label><div className="flex gap-2"><span className="self-center text-slate-500">$</span><input type="number" value={amount} onChange={e=>setAmount(+e.target.value||0)} className="flex-1"/></div></div>
            <div className="field"><label>Interest rate</label><div className="flex gap-2"><input type="number" step="0.01" value={rate} onChange={e=>setRate(+e.target.value||0)} className="flex-1"/><span className="self-center text-slate-500">%</span></div></div>
            <div className="field"><label>Draw period</label><div className="flex gap-2"><input type="number" value={draw} onChange={e=>setDraw(+e.target.value||0)} className="flex-1"/><span className="self-center text-slate-500">years</span></div></div>
            <div className="field"><label>Repayment period</label><div className="flex gap-2"><input type="number" value={repay} onChange={e=>setRepay(+e.target.value||0)} className="flex-1"/><span className="self-center text-slate-500">years</span></div></div>
            <label className="flex items-center gap-2 text-sm mt-2"><input type="checkbox" checked={includeCosts} onChange={e=>setIncludeCosts(e.target.checked)} data-testid="heloc-costs-cb"/> Include closing costs and fees</label>
            {includeCosts && <div className="field mt-2"><label>Closing costs</label><div className="flex gap-2"><span className="self-center text-slate-500">$</span><input type="number" value={costs} onChange={e=>setCosts(+e.target.value||0)} className="flex-1"/></div></div>}
            <label className="flex items-center gap-2 text-sm mt-2"><input type="checkbox" checked={showMore} onChange={e=>setShowMore(e.target.checked)} data-testid="heloc-more-cb"/> Show more options (biweekly + PMI)</label>
            {showMore && (
              <div className="mt-2 p-3 border border-slate-200 rounded bg-slate-50/60 space-y-2">
                <div className="field !mb-2"><label>Property tax (per year)</label><div className="flex gap-2"><span className="self-center text-slate-500">$</span><input type="number" value={propTax} onChange={e=>setPropTax(+e.target.value||0)} className="flex-1"/></div></div>
                <div className="field !mb-2"><label>Home insurance (per year)</label><div className="flex gap-2"><span className="self-center text-slate-500">$</span><input type="number" value={homeIns} onChange={e=>setHomeIns(+e.target.value||0)} className="flex-1"/></div></div>
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={biweekly} onChange={e=>setBiweekly(e.target.checked)}/> Show biweekly payback</label>
                {biweekly && (
                  <div className="text-xs text-slate-600 pl-6">Biweekly = {fmt(c.repayMonthly / 2)} every 2 weeks (26 payments/yr)</div>
                )}
              </div>
            )}
          </div>
          <div>
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div className="result-panel"><div className="result-title">Draw period monthly pay</div><div className="result-value" style={{fontSize:'1.4rem'}} data-testid="heloc-draw">{fmt(c.drawMonthly)}</div></div>
              <div className="result-panel"><div className="result-title">Repayment period monthly pay</div><div className="result-value" style={{fontSize:'1.4rem'}} data-testid="heloc-repay">{fmt(c.repayMonthly)}</div></div>
            </div>
            <table className="data-table"><tbody>
              <tr><td>Total of {c.totalPmts} payments</td><td className="text-right font-semibold">{fmt(c.totalPaid)}</td></tr>
              <tr><td>Total interest</td><td className="text-right text-orange-600">{fmt(c.totalInt)}</td></tr>
            </tbody></table>
            <div className="mt-4"><PieChart parts={[{label:'Loan amount', value:amount, color:'#2563eb'},{label:'Interest', value:c.totalInt, color:'#10b981'}]} size={170}/></div>
          </div>
        </div>

        <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div><h3 className="font-bold text-slate-900 mb-3">Repayment Amortization</h3><AmortTable schedule={c.amort}/></div>
          <div><h3 className="font-bold text-slate-900 mb-3">Balance / Interest / Payment</h3><BalanceGraph yearly={c.amort.yearly} startBalance={amount}/></div>
        </div>

        <hr className="my-8 border-slate-200" />
        <BorrowableLineOfCredit/>
      </CalcShell>
    </>
  );
}

/* Sub-calc: The amount of line of credit you can borrow */
function BorrowableLineOfCredit() {
  const [homeVal, setHomeVal] = useState(600000);
  const [mortBal, setMortBal] = useState(250000);
  const [ltv, setLtv] = useState(80);
  const c = useMemo(() => {
    const maxLine = Math.max(0, homeVal * ltv/100 - mortBal);
    const currentLtv = homeVal > 0 ? (mortBal / homeVal) * 100 : 0;
    return { maxLine, currentLtv };
  }, [homeVal, mortBal, ltv]);
  return (
    <div>
      <h3 className="text-lg font-bold text-slate-900 mb-1">The amount of line of credit you can borrow</h3>
      <p className="text-sm text-slate-600 mb-4">Use the calculator below to estimate the maximum home equity line of credit amount you may be able to borrow, based on the value of your home, your remaining mortgage balance, and the loan-to-value (LTV) ratio acceptable by the lender.</p>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div>
          <div className="field"><label>Current value of your house</label><div className="flex gap-2"><span className="self-center text-slate-500">$</span><input type="number" value={homeVal} onChange={e=>setHomeVal(+e.target.value||0)} className="flex-1" data-testid="heloc-loc-value"/></div></div>
          <div className="field"><label>Outstanding balance of your mortgage</label><div className="flex gap-2"><span className="self-center text-slate-500">$</span><input type="number" value={mortBal} onChange={e=>setMortBal(+e.target.value||0)} className="flex-1"/></div></div>
          <div className="field"><label>LTV ratio acceptable by the lender</label>
            <select value={ltv} onChange={e=>setLtv(+e.target.value)} data-testid="heloc-loc-ltv">
              {[60, 65, 70, 75, 80, 85, 90, 95].map(v => <option key={v} value={v}>{v}%</option>)}
            </select>
          </div>
        </div>
        <div>
          <div className="cnet-result-hdr">Result</div>
          <div className="cnet-result-body">
            <p className="text-lg mb-2">You may borrow up to <b className="text-green-700" data-testid="heloc-loc-borrow">{fmt(c.maxLine)}</b>.</p>
            <p className="text-sm text-slate-700 mb-3">Your current loan-to-value ratio: <b>{c.currentLtv.toFixed(1)}%</b>.</p>
            <p className="text-xs text-slate-500">There are other factors that may affect the home equity line of credit amount you qualify for, such as your credit history. Applicants with a credit score below 630 may not qualify. Your other debts can also affect the amount you are eligible for. In general, lenders typically won't approve a loan for borrowers with a debt-to-income ratio of 43% or higher.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
